import { useEffect, useMemo, useState } from "react";
import { FiCalendar, FiCheck, FiDatabase, FiDollarSign, FiHash, FiX } from "react-icons/fi";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import { getConfigByKey } from "./ConverstionRateType";

const ConversionRateModal = ({
    isOpen,
    onClose,
    rateKey,
    rateLabel,
    detailedRate,
    loading,
}) => {
    const [activeTab, setActiveTab] = useState("numerator");
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const [selectedItem, setSelectedItem] = useState([]);
    const [paginationData, setPaginationData] = useState({
        totalItems: 0,
        from: 0,
        to: 0,
        totalPages: 0,
    });

    // Reset states when modal opens
    useEffect(() => {
        if (isOpen) {
            setActiveTab("numerator");
            setCurrentPage(1);
        }
    }, [isOpen, rateKey]);

    const config = getConfigByKey(rateKey);
    const stageData = detailedRate?.stage;
    const innerData = stageData?.data || {};

    const rate = stageData?.rate || detailedRate?.rate || "0%";
    const numeratorCount = innerData?.numerator_count || 0;
    const denominatorCount = innerData?.denominator_count || 0;

    const numeratorData = innerData?.numerator_data?.data || [];
    const denominatorData = innerData?.denominator_data?.data || [];

    // Get total count from pagination meta (NOT the whole object)
    const numeratorTotal = innerData?.numerator_data?.total || 0;
    const denominatorTotal = innerData?.denominator_data?.total || 0;

    const totalCount = activeTab === "numerator" ? numeratorTotal : denominatorTotal;

    // Ensure currentData is always an array
    const currentData = useMemo(() => {
        const data = activeTab === "numerator" ? numeratorData : denominatorData;
        return Array.isArray(data) ? data : [];
    }, [activeTab, numeratorData, denominatorData]);

    const totalPages = Math.ceil(totalCount / itemsPerPage) || 1;

    // Paginated data
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return currentData.slice(start, start + itemsPerPage);
    }, [currentData, currentPage, itemsPerPage]);

    // Update pagination info
    useEffect(() => {
        setPaginationData({
            totalItems: totalCount,
            from: totalCount === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1,
            to: Math.min(currentPage * itemsPerPage, totalCount),
            totalPages: totalPages,
        });
    }, [totalCount, currentPage, itemsPerPage, totalPages]);

    const handleNext = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const handlePrev = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const formatCurrency = (value) => {
        if (!value || value === "N/A") return "N/A";
        return `$${parseFloat(value).toLocaleString()}`;
    };

    const getTableHeaders = () => {
        const base = [
            { key: "id", label: "ID", icon: FiHash },
            { key: "property", label: "Property", icon: FiDatabase },
            { key: "created", label: "Created At", icon: FiCalendar },
        ];

        if (activeTab === "numerator" && (rateKey?.includes("loi") || rateKey?.includes("executed"))) {
            base.splice(2, 0, { key: "contract_price", label: "Contract Price", icon: FiDollarSign });
        }

        if (activeTab === "denominator") {
            base.splice(2, 0, { key: "portfolio", label: "Portfolio", icon: FiCheck });
        }

        return base;
    };

    const renderCell = (header, record) => {
        switch (header.key) {
            case "id":
                return <span className="font-medium text-gray-900">{record.id}</span>;

            case "property":
                return (
                    <span className="text-gray-700 font-medium">
                        {record.property_name || `Property #${record.property_id || record.id}`} <br />
                        {record?.property?.street_address} <br />
                        {record?.property?.state && record?.property?.city ? `${record?.property?.city}, ${record?.property?.state}` : ""}
                    </span>
                );

            case "contract_price":
                return (
                    <span className="text-gray-700 font-medium">
                        {formatCurrency(record.contract_price)}
                    </span>
                );

            case "portfolio":
                return (
                    <span
                        className={`inline-flex px-2 py-1 text-xs rounded-full ${record.portfolio
                            ? "bg-blue-100 text-blue-700"
                            : "bg-gray-100 text-gray-600"
                            }`}
                    >
                        {record.portfolio ? "Yes" : "No"}
                    </span>
                );

            case "created":
                return <span className="text-gray-600">{formatDate(record.created_at)}</span>;

            default:
                return "N/A";
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-100">
                    <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                            <h2 className="text-lg font-semibold text-gray-900">{rateLabel}</h2>
                            <span className="text-xl font-bold text-blue-600">{rate}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm">
                            <span className="bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-md text-xs font-medium">
                                {config?.numeratorLabel || "Numerator"}: {numeratorCount}
                            </span>
                            <span className="bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md text-xs font-medium">
                                {config?.denominatorLabel || "Denominator"}: {denominatorCount}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <FiX className="w-5 h-5 text-gray-400" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 px-6">
                    <button
                        onClick={() => {
                            setActiveTab("numerator");
                            setCurrentPage(1);
                        }}
                        className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${activeTab === "numerator"
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        <span className="flex items-center gap-2">
                            <FiCheck className="w-4 h-4" />
                            {config?.numeratorLabel || "Numerator"}
                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                                {numeratorCount}
                            </span>
                        </span>
                    </button>

                    <button
                        onClick={() => {
                            setActiveTab("denominator");
                            setCurrentPage(1);
                        }}
                        className={`py-4 px-6 text-sm font-medium border-b-2 transition-colors ${activeTab === "denominator"
                            ? "border-blue-500 text-blue-600"
                            : "border-transparent text-gray-500 hover:text-gray-700"
                            }`}
                    >
                        <span className="flex items-center gap-2">
                            <FiDatabase className="w-4 h-4" />
                            {config?.denominatorLabel || "Denominator"}
                            <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full text-xs">
                                {denominatorCount}
                            </span>
                        </span>
                    </button>
                </div>

                {/* Table Section */}
                <div className="flex-1 overflow-auto px-6 pb-6">
                    {loading ? (
                        <Loader />
                    ) : currentData.length === 0 ? (
                        <p className="body-N text-center mt-6 p-10">No Record Found</p>
                    ) : (
                        <>
                            <table className="contact-table light-bg-L w-full border-collapse">
                                <thead className="sticky top-0 z-20">
                                    <tr>
                                        {getTableHeaders().map((header) => (
                                            <th
                                                key={header.key}
                                                className="body-N dark-M py-3 px-4 text-left bg-inherit border-b"
                                            >
                                                <span className="flex items-center gap-1">
                                                    {header.icon && (
                                                        <header.icon className="w-3 h-3" />
                                                    )}
                                                    {header.label}
                                                </span>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {paginatedData.map((record) => (
                                        <tr
                                            key={record.id}
                                            className="border-b last:border-b-0 hover:bg-gray-50"
                                        >
                                            {getTableHeaders().map((header) => (
                                                <td
                                                    key={header.key}
                                                    className="dark-M body-S py-3 px-4"
                                                >
                                                    {renderCell(header, record)}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            <ContactPagination
                                from="converstionrate"
                                setSelectedItem={setSelectedItem}
                                selectedItem={selectedItem}
                                paginationData={paginationData}
                                handleNext={handleNext}
                                handlePrev={handlePrev}
                                itemPerPage={itemsPerPage}
                                handleItemPerPageSelect={(value) => {
                                    setItemsPerPage(value);
                                    setCurrentPage(1);
                                }}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConversionRateModal;